import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const CourseCard = ({ course }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-success bg-success/10 border-success/20';
      case 'intermediate':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'advanced':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all">
      <div className="grid grid-cols-12 gap-6">
        {/* Thumbnail */}
        <div className="col-span-12 md:col-span-4">
          <div className="relative h-full min-h-[200px] bg-muted">
            <img
              src={course?.thumbnail}
              alt={course?.alt}
              className="w-full h-full object-cover"
            />
            {course?.completed && (
              <div className="absolute top-3 right-3 bg-success text-success-foreground px-3 py-1 rounded-full flex items-center gap-1.5 text-xs font-semibold">
                <Icon name="CheckCircle" size={14} color="currentColor" />
                Completed
              </div>
            )}
            {course?.certification && (
              <div className="absolute bottom-3 left-3 bg-amber-500 text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-xs font-semibold">
                <Icon name="Award" size={14} color="white" />
                Certification
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="col-span-12 md:col-span-8 p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {course?.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {course?.description}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className={cn(
              'px-3 py-1 rounded-full border text-xs font-semibold capitalize',
              getDifficultyColor(course?.difficulty)
            )}>
              {course?.difficulty}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} color="currentColor" />
              <span>{course?.duration}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Icon name="BookOpen" size={16} color="currentColor" />
              <span>{course?.modules} modules</span>
            </div>
          </div>

          {/* Progress Bar */}
          {course?.progress > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-foreground">Progress</span>
                <span className="text-xs font-bold text-primary">{course?.progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-500"
                  style={{ width: `${course?.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {course?.completed ? (
              <>
                <Button variant="outline" iconName="RotateCcw">
                  Review Course
                </Button>
                {course?.certification && (
                  <Button variant="default" iconName="Download">
                    Download Certificate
                  </Button>
                )}
              </>
            ) : course?.progress > 0 ? (
              <>
                <Button variant="default" iconName="Play">
                  Continue Learning
                </Button>
                <Button variant="outline" iconName="RotateCcw">
                  Restart
                </Button>
              </>
            ) : (
              <>
                <Button variant="default" iconName="Play">
                  Start Course
                </Button>
                <Button variant="outline" iconName="BookmarkPlus">
                  Save for Later
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;